import {Module} from 'vuex'
import _ from 'lodash'
import {ComposeRequest, Resolution} from '@/types/conductor'
import conductorService from '@/api/conductorService'
import {
	BetslipInfoRequest,
	BetslipInfoResponse,
	BetslipLeg,
	EventMarketOutcomeSelector,
} from '@/types/client'
import clientService from '@/api/clientService'
import {eventBus} from '@/utils/eventBus'
import { getBaseRequestData, getLocale, getScopes, getInferredCurrency } from '@/utils/conductor.util'

import { useProfilesStore } from '@/store/modules/userProfile'

function initialState() {
	return {
		context: '',
		query: '',

		composeResponse: {},
		lastQuery: '',

		isFetchingBetBuilderResponse: false,
		isFetchingBetslipInfo: false,
		betBuilderResponse: {},

		marketDataResponses: [],

		deepLinkResponse: {},

		betslipInfoResponse: {},
		legPriceMap: {},
		selectionPriceMap: {},

		betslip: {},

		// Flag to show if the bet builder matrix has been edited at all.
		// true if any changes have been made
		// false if not
		isBetBuilderCriteriaDirty: false,
		isRestoring: false,
	}
}

const betBuilderModule: Module<any, any> = {
	namespaced: true,

	state: initialState,

	mutations: {
		resetBetBuilderStore(state) {
			const initial: any = initialState()
			Object.keys(initial).forEach((key) => {
				if (key === 'context') return  // Skip context
				if (key in state) {
				state[key] = initial[key]
				}
			})
			},

		setContext(state, context) {
			state.context = context
		},

		setLastQuery(state, lastQuery) {
			state.lastQuery = lastQuery
		},
		setQuery(state, query) {
			state.query = query
		},

		setComposeResponse(state, composeResponse) {
			state.composeResponse = composeResponse
		},

		setStake(state, stake) {
			if (!state.betslip.stake) {
				state.betslip.stake = {
					// FIXME
					currency: 'GBP',
				}
			}

			state.betslip.stake.amount = stake
		},

		setIsFetchingBetBuilderResponse(state: any, isFetching: boolean) {
			state.isFetchingBetBuilderResponse = isFetching
		},
		setIsFetchingBetslipInfo(state: any, isFetching: boolean) {
			state.isFetchingBetslipInfo = isFetching
		},
		setIsRestoring(state: any, isRestoring: boolean) {
			state.isRestoring = isRestoring
		},

		setBetBuilderResponse(state, betBuilderResponse) {
			state.betBuilderResponse = betBuilderResponse
		},

		setMarketDataResponses(state, marketDataResponses) {
			state.marketDataResponses = marketDataResponses
		},

		setDeepLinkResponse(state, deepLinkResponse) {
			state.deepLinkResponse = deepLinkResponse
		},

		setBetslipInfoResponse(state, betslipInfoResponse) {
			state.betslipInfoResponse = betslipInfoResponse
		},

		setLegPriceMap(state, legPriceMap) {
			state.legPriceMap = legPriceMap
		},

		setSelectionPriceMap(state, selectionPriceMap) {
			state.selectionPriceMap = selectionPriceMap
		},

		setBetslip(state, betslip) {
			state.betslip = betslip
		},

		setIsBetBuilderCriteriaDirty(state, isBetBuilderCriteriaDirty) {
			state.isBetBuilderCriteriaDirty = isBetBuilderCriteriaDirty
		},

		REMOVE_LEG_OPTIMISTIC(state, legIndex) {
			if (state.betBuilderResponse?.legs) {
				state.betBuilderResponse.legs.splice(legIndex, 1)
			}
		},

		REMOVE_MARKET_OPTIMISTIC(state, { legIndex, marketIndex }) {
			const leg = state.betBuilderResponse?.legs?.[legIndex]
			if (leg?.legCriteria?.marketSelectorConfigs) {
				leg.legCriteria.marketSelectorConfigs.splice(marketIndex, 1)
			}
		},
	},

	actions: {
		resetBetBuilderStore({commit}) {
			commit('resetBetBuilderStore')
		},

		setContext({ commit, state, dispatch }, context) {
			if (state.context && state.context !== context) {
				dispatch('resetBetBuilderStore')
			}
			commit('setContext', context)
		},

		setQuery({commit}, query) {
			commit('setQuery', query)
		},

		async getComposeResponse({commit, dispatch, getters}) {
			commit('setComposeResponse', {})
			const locale: string = getLocale()
			const profilesStore = useProfilesStore()

			const composeRequest: ComposeRequest = {
				query: getters.query,
				locale: locale,
				defaultCurrency: profilesStore.profile.preferredCurrency || getInferredCurrency()
			}

			const scopes: string[] | null = getScopes()
			if (scopes != null) {
				composeRequest.scopes = scopes
			}

			const composeResponse = //
					await conductorService.getCompose(getters.context, composeRequest)
			commit('setComposeResponse', composeResponse)
			commit('setBetslip', getters.resolution.betslip)
			commit('setIsBetBuilderCriteriaDirty', false)

			eventBus.emit(composeResponse.traceId)

			if (!getters.isBetBuilder) {
				await dispatch('getBetslipInfo')
			}
		},

		setComposeResponse({commit, getters}, composeResponse) {
			commit('setComposeResponse', composeResponse)
			commit('setBetslip', getters.resolution.betslip)
			eventBus.emit(composeResponse.traceId)
		},

		async setStake({commit, dispatch, getters}, stake) {
			commit('setStake', stake)
			await dispatch('getBetslipInfo')
		},

		async getBetslipInfo({commit, dispatch, getters}) {
			commit('setIsFetchingBetslipInfo', true)
			try {
				if (getters.isBetBuilder) {
					await dispatch('getBetslipInfoFromBetBuilder')
				} else {
					await dispatch('getBetslipInfoFromCompose')
				}
			} finally {
				commit('setIsFetchingBetslipInfo', false)
			}
		},

		clearBetslipInfo({commit}) {
			commit('setBetslipInfoResponse', {})
			commit('setLegPriceMap', {})
			commit('setSelectionPriceMap', {})
		},

		buildPriceMaps({commit, getters}) {
			const legPriceMap = {} as any
			const selectionPriceMap = {} as any
			getters.betslipInfoResponse.legs?.forEach((leg: any) => {
				legPriceMap[leg.event.eventId] = leg.price

				leg.selections.forEach((selection: any) => {
					selectionPriceMap[selection.outcome.outcomeId] =
							selection.outcome.price
				})
			})
			commit('setLegPriceMap', legPriceMap)
			commit('setSelectionPriceMap', selectionPriceMap)
		},

		async getBetslipInfoFromCompose({
																			commit,
																			dispatch,
																			getters,
																		}): Promise<void> {
			dispatch('clearBetslipInfo')

			const betslip = getters.betslip
			if( !betslip ) {
				return
			}
			
			const stake = betslip.stake
			const type = betslip.type
			const option = betslip.option

			const legs: BetslipLeg[] = []
			const selectionGroups = betslip.selectionGroups ?? []
			for (const selectionGroup of selectionGroups) {
				const legSelectors = []

				const eventId = selectionGroup.event?.bookieId
				const selections = selectionGroup.selections ?? []
				for (const selection of selections) {
					const selector: EventMarketOutcomeSelector = {
						eventId: eventId,
						marketId: selection.book?.bookieId,
						outcomeId: selection.outcome?.bookieId,
					}

					if (selector.eventId && selector.marketId && selector.outcomeId) {
						legSelectors.push(selector)
					}
				}

				const betslipLeg: BetslipLeg = {
					selections: legSelectors,
				}
				legs.push(betslipLeg)
			}

			const betslipInfoRequest: BetslipInfoRequest = {
				stake: stake,
				betType: type,
				legs: legs,
				betOption: option,
			}

			const betslipInfoResponse: BetslipInfoResponse =
					await clientService.getBetslipInfo(getters.context, betslipInfoRequest)
			commit('setBetslipInfoResponse', betslipInfoResponse)
			dispatch('buildPriceMaps')
		},

		async getBetslipInfoFromBetBuilder({
																				 commit,
																				 getters,
																				 dispatch,
																			 }): Promise<void> {
			dispatch('clearBetslipInfo')

			const betslip = getters.betslip
			const stake = betslip.stake
			const type = betslip.type
			const option = betslip.option

			const legs = []
			const betBuilderResponseLegs = getters.betBuilderResponse?.legs ?? []
			for (let leg of betBuilderResponseLegs) {
				const legSelectors = []
				const betslipSelectionGroupSelections =
						leg?.betslipSelectionGroup?.selections ?? []
				for (let selection of betslipSelectionGroupSelections) {
					let selector: EventMarketOutcomeSelector = {
						eventId: leg?.betslipSelectionGroup?.event?.bookieId,
						marketId: selection?.book?.bookieId,
						outcomeId: selection?.outcome?.bookieId,
					}

					if (selector.eventId && selector.marketId && selector.outcomeId) {
						legSelectors.push(selector)
					}
				}

				const betslipLeg: BetslipLeg = {
					selections: legSelectors,
				}
				legs.push(betslipLeg)
			}

			const betslipInfoRequest: BetslipInfoRequest = {
				stake: stake,
				betType: type,
				legs: legs,
				betOption: option,
			}

			const betslipInfoResponse: BetslipInfoResponse =
					await clientService.getBetslipInfo(getters.context, betslipInfoRequest)
			commit('setBetslipInfoResponse', betslipInfoResponse)
			dispatch('buildPriceMaps')
		},

		async getBetBuilderResponse({commit, getters, dispatch}, context) {
			commit('setIsFetchingBetBuilderResponse', true)

			const betslip = {
				stake: getters.betslip.stake,
				type: getters.betslip.type,
				option: getters.betslip.option,
			}
			const legSelectors = getters.betBuilderCriteria?.bbLegSelectors

			if (legSelectors !== undefined) {
				const betBuilderBuildRequest = {
					betslip: betslip,
					legSelectors: legSelectors,
				}

				const betBuilderResponse = await conductorService.getBetBuilder(
						getters.context,
						betBuilderBuildRequest,
				)
				commit('setIsFetchingBetBuilderResponse', false)
				commit('setBetBuilderResponse', betBuilderResponse)

				if (betBuilderResponse.betslip && Object.keys(betBuilderResponse.betslip).length > 0) {
					commit('setBetslip', betBuilderResponse.betslip)
				} else {
					commit('setBetslip', {})
				}

				await dispatch('getBetslipInfo')
			} else {
				commit('setIsFetchingBetBuilderResponse', false)
				commit('setBetBuilderResponse', {})
				commit('setBetslipInfoResponse', {})
			}
		},

		async restoreSharedState({dispatch, commit, getters}, {stateJson, query}) {
			console.log('restoreSharedState started with:', stateJson, 'query:', query)
			if (!stateJson) return
			try {
				const state = typeof stateJson === 'object' ? stateJson : JSON.parse(stateJson)
				if (query) {
					commit('setQuery', query)
				}
				await dispatch('getComposeResponse')

				let bbLegSelectors = state.bbLegSelectors
				if (!bbLegSelectors && state.betSlipImageData && state.betSlipImageData.events) {
					// Fallback to generating selectors from the events data if criteria selectors aren't directly available
					bbLegSelectors = state.betSlipImageData.events.map((event: any, index: number) => {
						return {
							nodeSelector: { type: 'SOCCER_FO' },
							eventSelector: { bookieId: event.bookieId || String(index) },
							legMarketCriteria: {
								marketSelectors: (event.selections || []).map((sel: any) => {
									return {
										marketTypeName: sel.bookLabel || '',
										marketConditions: [
											{ marketDimensionName: 'YES_NO', value: sel.outcome || '' }
										]
									}
								})
							}
						}
					})
				}

				if (bbLegSelectors) {
					console.log('Injecting bbLegSelectors:', bbLegSelectors)
					const composeResponse = JSON.parse(JSON.stringify(getters.composeResponse))
					if (!composeResponse.resolutions) {
						composeResponse.resolutions = [{}]
					}
					if (!composeResponse.resolutions[0]) {
						composeResponse.resolutions[0] = {}
					}
					
					const resolution = composeResponse.resolutions[0]
					resolution.type = 'BET_BUILDER'
					if (!resolution.betBuilderCriteria) {
						resolution.betBuilderCriteria = {}
					}
					const initialSelectors = JSON.parse(JSON.stringify(getters.betBuilderCriteria?.bbLegSelectors || []))
					
					resolution.betBuilderCriteria.bbLegSelectors = bbLegSelectors

					const isDirty = !_.isEqual(initialSelectors, bbLegSelectors)
					commit('setIsBetBuilderCriteriaDirty', isDirty)
					
					commit('setComposeResponse', composeResponse)
					await dispatch('getBetBuilderResponse')
					await dispatch('getBetslipInfo')
				}
			} catch (err) {
				console.error('Failed to restore shared state', err)
			}
		},

		async updateBetBuilderDimension(
				{commit, getters, dispatch},
				{legIndex, marketIndex, dimensionIndex, marketDimensionNewValue},
		) {
			const legSelectors = getters.betBuilderCriteria.bbLegSelectors
			const legSelector = legSelectors[legIndex]

			const marketSelectors = legSelector.legMarketCriteria.marketSelectors
			const marketSelector = marketSelectors[marketIndex]

			if (!marketSelector.marketConditions) {
				const marketConditionConfigs =
						getters.betBuilderResponse?.legs[legIndex]?.legCriteria
								?.marketSelectorConfigs[marketIndex]?.marketConditionConfigs

				const marketConditions = []
				for (const marketConditionConfig of marketConditionConfigs) {
					const marketDimensionName = marketConditionConfig.marketDimensionName

					const value = marketConditionConfig.value
							? marketConditionConfig.value
							: marketConditionConfig.pickDimensionValueConfigs[0]

					const marketCondition = {
						marketDimensionName,
						value,
					}

					marketConditions.push(marketCondition)
				}

				marketSelector.marketConditions = marketConditions
			}

			const marketConditions = marketSelector.marketConditions
			const marketCondition = marketConditions[dimensionIndex]

			marketCondition.value = marketDimensionNewValue

			const betslip = {
				stake: getters.betslip.stake,
				type: getters.betslip.type,
				option: getters.betslip.option,
			}
			const betBuilderBuildRequest = {
				betslip: betslip,
				legSelectors: legSelectors,
			}

			const betBuilderResponse = await conductorService.getBetBuilder(
					getters.context,
					betBuilderBuildRequest,
			)
			commit('setBetBuilderResponse', betBuilderResponse)
			commit('setBetslip', betBuilderResponse.betslip)
			commit('setIsBetBuilderCriteriaDirty', true)

			await dispatch('getBetslipInfo')
		},

		async changeEvent({commit, getters, dispatch}, {legIndex, event}) {
			const legSelectors = getters.betBuilderCriteria.bbLegSelectors
			const legSelector = legSelectors[legIndex]
			legSelector.eventSelector = {
				bookieId: event.event.bookieId,
			}

			const betslip = {
				stake: getters.betslip.stake,
				type: getters.betslip.type,
				option: getters.betslip.option,
			}
			const betBuilderBuildRequest = {
				betslip: betslip,
				legSelectors: legSelectors,
			}

			const betBuilderResponse = await conductorService.getBetBuilder(
					getters.context,
					betBuilderBuildRequest,
			)
			commit('setBetBuilderResponse', betBuilderResponse)
			commit('setBetslip', betBuilderResponse.betslip)
			commit('setIsBetBuilderCriteriaDirty', true)

			await dispatch('getBetslipInfo')
		},

		async addMarket({commit, getters, dispatch}, {legIndex, market, markets}) {
			const legSelectors = getters.betBuilderCriteria.bbLegSelectors
			const legSelector = legSelectors[legIndex]
			const legMarketCriteria = legSelector.legMarketCriteria
			const marketSelectors = legMarketCriteria.marketSelectors

			const marketsList = markets || (Array.isArray(market) ? market : (market ? [market] : []))
			
			const betslip = {
				stake: getters.betslip.stake,
				type: getters.betslip.type,
				option: getters.betslip.option,
			}

			for (const m of marketsList) {
				const newMarket = {
					marketTypeName: m.marketTypeName,
				}
				marketSelectors.push(newMarket)

				const betBuilderBuildRequest = {
					betslip: betslip,
					legSelectors: legSelectors,
				}

				const betBuilderResponse = await conductorService.getBetBuilder(
						getters.context,
						betBuilderBuildRequest,
				)
				commit('setBetBuilderResponse', betBuilderResponse)
				commit('setBetslip', betBuilderResponse.betslip)

				const marketIndex = marketSelectors.length - 1
				const marketSelector = marketSelectors[marketIndex]

				const marketConditionConfigs =
						getters.betBuilderResponse?.legs[legIndex]?.legCriteria
								?.marketSelectorConfigs[marketIndex]?.marketConditionConfigs

				if (marketConditionConfigs) {
					const marketConditions = []
					for (const marketConditionConfig of marketConditionConfigs) {
						const marketDimensionName = marketConditionConfig.marketDimensionName

						const value = marketConditionConfig.value
								? marketConditionConfig.value
								: marketConditionConfig.pickDimensionValueConfigs?.[0]?.value

						const marketCondition = {
							marketDimensionName,
							value,
						}

						marketConditions.push(marketCondition)
					}

					marketSelector.marketConditions = marketConditions

					const betBuilderBuildRequestWithDefaultValue = {
						betslip: betslip,
						legSelectors: legSelectors,
					}

					const betBuilderBuildResponseWithDefaultValue =
							await conductorService.getBetBuilder(
									getters.context,
									betBuilderBuildRequestWithDefaultValue,
							)
					commit('setBetBuilderResponse', betBuilderBuildResponseWithDefaultValue)
					commit('setBetslip', betBuilderBuildResponseWithDefaultValue.betslip)
				}
			}

			commit('setIsBetBuilderCriteriaDirty', true)

			await dispatch('getBetslipInfo')
		},

		async removeLeg({commit, getters, dispatch}, legIndex) {
			commit('REMOVE_LEG_OPTIMISTIC', legIndex)
			const legSelectors = getters.betBuilderCriteria.bbLegSelectors
			legSelectors.splice(legIndex, 1)

			const betslip = {
				stake: getters.betslip.stake,
				type: getters.betslip.type,
				option: getters.betslip.option,
			}
			const betBuilderBuildRequest = {
				betslip: betslip,
				legSelectors: legSelectors,
			}

			const betBuilderResponse = await conductorService.getBetBuilder(
					getters.context,
					betBuilderBuildRequest,
			)
			commit('setBetBuilderResponse', betBuilderResponse)
			commit('setBetslip', betBuilderResponse.betslip)
			commit('setIsBetBuilderCriteriaDirty', true)

			await dispatch('getBetslipInfo')
		},

		async removeMarket(
				{commit, getters, dispatch},
				{legIndex, marketIndex},
		) {
			commit('REMOVE_MARKET_OPTIMISTIC', { legIndex, marketIndex })
			const legSelectors = getters.betBuilderCriteria.bbLegSelectors
			const legSelector = legSelectors[legIndex]
			const legMarketCriteria = legSelector.legMarketCriteria
			const marketSelectors = legMarketCriteria.marketSelectors
			marketSelectors.splice(marketIndex, 1)

			const betslip = {
				stake: getters.betslip.stake,
				type: getters.betslip.type,
				option: getters.betslip.option,
			}
			const betBuilderBuildRequest = {
				betslip: betslip,
				legSelectors: legSelectors,
			}

			const betBuilderResponse = await conductorService.getBetBuilder(
					getters.context,
					betBuilderBuildRequest,
			)
			commit('setBetBuilderResponse', betBuilderResponse)
			commit('setBetslip', betBuilderResponse.betslip)
			commit('setIsBetBuilderCriteriaDirty', true)

			await dispatch('getBetslipInfo')
		},

		async duplicateMarket(
				{commit, getters, dispatch},
				{legIndex, marketIndex},
		) {
			const legSelectors = getters.betBuilderCriteria.bbLegSelectors
			const legSelector = legSelectors[legIndex]
			const legMarketCriteria = legSelector.legMarketCriteria
			const marketSelectors = legMarketCriteria.marketSelectors

			const marketSelectorToDuplicate = marketSelectors[marketIndex]
			if (!marketSelectorToDuplicate) return

			if (!marketSelectorToDuplicate.marketConditions) {
				const marketConditionConfigs =
						getters.betBuilderResponse?.legs[legIndex]?.legCriteria
								?.marketSelectorConfigs[marketIndex]?.marketConditionConfigs

				const marketConditions = []
				if (marketConditionConfigs) {
					for (const marketConditionConfig of marketConditionConfigs) {
						const marketDimensionName = marketConditionConfig.marketDimensionName
						const value = marketConditionConfig.value
								? marketConditionConfig.value
								: marketConditionConfig.pickDimensionValueConfigs?.[0]?.value

						marketConditions.push({
							marketDimensionName,
							value,
						})
					}
				}
				marketSelectorToDuplicate.marketConditions = marketConditions
			}

			const duplicatedMarketSelector = JSON.parse(JSON.stringify(marketSelectorToDuplicate))
			marketSelectors.splice(marketIndex + 1, 0, duplicatedMarketSelector)

			const betslip = {
				stake: getters.betslip.stake,
				type: getters.betslip.type,
				option: getters.betslip.option,
			}
			const betBuilderBuildRequest = {
				betslip: betslip,
				legSelectors: legSelectors,
			}

			const betBuilderResponse = await conductorService.getBetBuilder(
					getters.context,
					betBuilderBuildRequest,
			)
			commit('setBetBuilderResponse', betBuilderResponse)
			commit('setBetslip', betBuilderResponse.betslip)
			commit('setIsBetBuilderCriteriaDirty', true)

			await dispatch('getBetslipInfo')
		},

		setIsBetBuilderCriteriaDirty({commit}, isBetBuilderCriteriaDirty) {
			commit('setIsBetBuilderCriteriaDirty', isBetBuilderCriteriaDirty)
		},
	},

	getters: {
		context(state) {
			return state.context
		},

		query(state) {
			return state.query
		},

		lastQuery(state) {
			return state.lastQuery
		},

		composeResponse(state) {
			return state.composeResponse
		},

		traceId(state, getters) {
			return getters.composeResponse.traceId
		},

		stake(state, getters) {
			return getters.betslip?.stake?.amount
		},

		suggestions(state, getters) {
			if (getters.composeResponse?.suggestions) {
				return getters.composeResponse?.suggestions
			}

			return []
		},

		resolution(state, getters): Resolution {
			if (
					getters.composeResponse?.resolutions &&
					getters.composeResponse.resolutions[0]
			) {
				return getters.composeResponse.resolutions[0]
			}

			return {} as Resolution
		},

		betslip(state) {
			return state.betslip
		},

		isBetBuilder(state, getters) {
			return !!getters.betBuilderCriteria
		},

		betBuilderCriteria(state, getters) {
			return getters.resolution?.betBuilderCriteria
		},

		isFetchingBetBuilderResponse: (state: any) => state.isFetchingBetBuilderResponse,
		isFetchingBetslipInfo: (state: any) => state.isFetchingBetslipInfo,
		isRestoring: (state: any) => state.isRestoring,

		betBuilderResponse(state) {
			return state.betBuilderResponse
		},

		hasBetBuilderResponse(state, getters) {
			return Object.keys(getters.betBuilderResponse).length > 0
		},

		legs(state) {
			return state.betBuilderResponse.legs
		},

		leg: (state, getters) => (index: number) => {
			return getters.legs[index]
		},

		errors(state) {
			return state.betBuilderResponse.errors
		},

		deepLinkResponse(state) {
			return state.deepLinkResponse
		},

		betslipInfoResponse(state) {
			return state.betslipInfoResponse
		},

		betslipPrice(state, getters) {
			const formattedPrice = getters.betslipInfoResponse?.price?.formatted
			if (!formattedPrice) {
				return 'N/A'
			}

			return formattedPrice
		},

		betslipPriceDecimal(state, getters) {
			const decimalPrice = getters.betslipInfoResponse?.price?.decimal
			if (!decimalPrice) {
				return 'N/A'
			}

			return decimalPrice
		},

		legPriceMap(state, getters) {
			// const legPriceMap = {} as any
			//
			// const betslipInfo = getters.betslipInfoResponse
			// const legs = betslipInfo.legs
			//
			// legs.forEach((leg: any) => {
			//   legPriceMap[leg.event.eventId] = leg.price
			// })

			return state.legPriceMap
		},

		legPrice: (state, getters) => (eventId: string | number) => {
			const formattedPrice = getters.legPriceMap[eventId]?.formatted
			if (!formattedPrice) {
				return 'N/A'
			}

			return formattedPrice
		},

		selectionPriceMap(state, getters) {
			// const selectionPriceMap = {} as any
			//
			// const betslipInfo = getters.betslipInfoResponse
			// const legs = betslipInfo.legs
			//
			// legs.forEach((leg: any) => {
			//   leg.selections.forEach((selection: any) => {
			//     selectionPriceMap[selection.outcome.outcomeId] =
			//       selection.outcome.price
			//   })
			// })

			return state.selectionPriceMap
		},

		selectionPrice: (state, getters) => (outcomeId: string | number) => {
			const formattedPrice = getters.selectionPriceMap[outcomeId]?.formatted
			if (!formattedPrice) {
				return 'N/A'
			}

			return formattedPrice
		},

		deepLinkUrl(state, getters) {
			return getters.betslipInfoResponse?.deepLinkURL
		},

		isBetBuilderResponseDirty(state) {
			return state.isBetBuilderCriteriaDirty
		},
	},
}

export default betBuilderModule
